/**
 * WorkflowStateManager Component - The System Metronome
 * 
 * This component acts as the central orchestrator for all workflow states
 * across both customer and supplier cases. It provides:
 * - Visual state indication with progress tracking
 * - Contextual action buttons for available transitions
 * - Cross-workflow integration (supplier workflows within customer cases)
 * - Unified state management across the entire system
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  StepConnector,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  PlayArrow as NextIcon,
  CheckCircle as CompleteIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Settings as ConfigIcon,
  Assignment as ProcessIcon,
  Email as EmailIcon,
  Send as SendIcon,
  RateReview as QuoteIcon,
  ShoppingCart as OrderIcon,
  Build as CreateIcon,
  Phone as ContactIcon
} from '@mui/icons-material';
import { useNotification } from '../../../contexts/NotificationContext';

// Workflow state definitions (matching backend)
type WorkflowState = 
  | 'case_created'
  | 'preprocessing_decision'
  | 'preprocessing_needed'
  | 'waiting_for_customer_decision'
  | 'case_won'
  | 'case_lost'
  | 'waiting_for_file_upload'
  | 'completed'
  | 'supplier_case_created'
  | 'supplier_quote_generated'
  | 'supplier_contacted'
  | 'supplier_response_received'
  | 'quote_selected'
  | 'converted_to_order'
  | 'case_closed';
const CUSTOMER_WORKFLOW_STATES = [
  'case_created',                    // 1. Initial case creation
  'preprocessing_decision',          // 2. Decision: Is preprocessing needed?
  'preprocessing_needed',            // 3. Need to add supplier quotes to positions
  'waiting_for_customer_decision',   // 4. Customer decides case outcome
  'case_won',                       // 5. Customer approved - convert to order
  'case_lost',                      // 6. Customer declined - close case
  'waiting_for_file_upload',        // 7. Won: waiting for complete files
  'completed'                       // 8. Final state
];

const SUPPLIER_WORKFLOW_STATES = [
  'supplier_case_created',           // 1. Initial creation
  'supplier_quote_generated',        // 2. Quote created, ready to send
  'supplier_contacted',              // 3. Email sent, waiting for response
  'supplier_response_received',      // 4. Response received, waiting for selection
  'quote_selected',                  // 5. Quote selected, ready for conversion
  'converted_to_order',             // 6. Converted to order (enters order workflow)
  'case_closed'                     // 7. Closed without conversion
];

// Supplier workflow visual steps (for progress display)
const SUPPLIER_WORKFLOW_STEPS = [
  { 
    key: 'supplier_case_created', 
    label: 'Fall erstellt', 
    icon: CreateIcon, 
    color: '#757575',
    description: 'Lieferantenfall wurde erstellt'
  },
  { 
    key: 'supplier_quote_generated', 
    label: 'Anfrage generiert', 
    icon: QuoteIcon, 
    color: '#1976d2',
    description: 'Preisanfrage wurde erstellt und ist versandbereit'
  },
  { 
    key: 'supplier_contacted', 
    label: 'Kontaktiert', 
    icon: ContactIcon, 
    color: '#ed6c02',
    description: 'E-Mail an Lieferant gesendet'
  },
  { 
    key: 'supplier_response_received', 
    label: 'Antwort erhalten', 
    icon: EmailIcon, 
    color: '#f57c00',
    description: 'Lieferant-Antwort empfangen'
  },
  { 
    key: 'quote_selected', 
    label: 'Angebot ausgewählt', 
    icon: CompleteIcon, 
    color: '#2e7d32',
    description: 'Angebot wurde ausgewählt'
  },
  { 
    key: 'converted_to_order', 
    label: 'Bestellung erstellt', 
    icon: OrderIcon, 
    color: '#388e3c',
    description: 'In Bestellung umgewandelt'
  }
];

// Styled components for visual progress
const WorkflowConnector = styled(StepConnector)(({ theme }) => ({
  '&.Mui-active': {
    '& .MuiStepConnector-line': {
      backgroundImage: 'linear-gradient(95deg, #1976d2 0%, #2e7d32 50%, #388e3c 100%)',
      height: 3,
    },
  },
  '&.Mui-completed': {
    '& .MuiStepConnector-line': {
      backgroundImage: 'linear-gradient(95deg, #1976d2 0%, #2e7d32 50%, #388e3c 100%)',
      height: 3,
    },
  },
}));

// Valid transitions (matching backend validation)
const CUSTOMER_TRANSITIONS: { [key: string]: string[] } = {
  'case_created': ['preprocessing_decision'],
  'preprocessing_decision': ['preprocessing_needed', 'waiting_for_customer_decision'],
  'preprocessing_needed': ['waiting_for_customer_decision'],
  'waiting_for_customer_decision': ['case_won', 'case_lost'],
  'case_won': ['waiting_for_file_upload'],
  'case_lost': ['completed'],
  'waiting_for_file_upload': ['completed'],
  'completed': []
};

const SUPPLIER_TRANSITIONS: { [key: string]: string[] } = {
  'supplier_case_created': ['supplier_quote_generated'],
  'supplier_quote_generated': ['supplier_contacted', 'case_closed'],
  'supplier_contacted': ['supplier_response_received', 'case_closed'],
  'supplier_response_received': ['quote_selected', 'case_closed'],
  'quote_selected': ['converted_to_order', 'case_closed'],
  'converted_to_order': [],
  'case_closed': []
};

// Supplier Workflow Progress Component
interface SupplierWorkflowProgressProps {
  currentState: string;
  showLabels?: boolean;
  size?: 'small' | 'medium' | 'large';
}

const SupplierWorkflowProgress: React.FC<SupplierWorkflowProgressProps> = ({
  currentState,
  showLabels = true,
  size = 'medium'
}) => {
  const currentStepIndex = SUPPLIER_WORKFLOW_STEPS.findIndex(step => step.key === currentState);
  const currentStep = SUPPLIER_WORKFLOW_STEPS[currentStepIndex];
  
  // Calculate progress percentage
  const progressPercentage = currentStepIndex >= 0 ? ((currentStepIndex + 1) / SUPPLIER_WORKFLOW_STEPS.length) * 100 : 0;

  if (!showLabels) {
    // Compact chip version
    return (
      <Chip
        icon={React.createElement(currentStep?.icon || CreateIcon)}
        label={currentStep?.label || currentState}
        sx={{
          backgroundColor: currentStep?.color || '#757575',
          color: 'white',
          fontWeight: 'bold'
        }}
        size={size === 'small' ? 'small' : 'medium'}
      />
    );
  }

  return (
    <Box sx={{ width: '100%', mb: 2 }}>
      <Typography variant="h6" gutterBottom>
        Lieferanten-Workflow Fortschritt
      </Typography>
      
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Fortschritt: {Math.round(progressPercentage)}% ({currentStepIndex + 1} von {SUPPLIER_WORKFLOW_STEPS.length} Schritten)
        </Typography>
        <LinearProgress 
          variant="determinate" 
          value={progressPercentage} 
          sx={{ flexGrow: 1, height: 6, borderRadius: 3 }}
        />
      </Box>
      
      <Stepper 
        activeStep={currentStepIndex} 
        connector={<WorkflowConnector />}
        sx={{ mb: 2 }}
      >
        {SUPPLIER_WORKFLOW_STEPS.map((step, index) => {
          const isActive = index === currentStepIndex;
          const isCompleted = index < currentStepIndex;
          
          return (
            <Step key={step.key} completed={isCompleted}>
              <StepLabel
                StepIconComponent={({ active, completed }) => (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      backgroundColor: active || completed ? step.color : '#e0e0e0',
                      color: active || completed ? 'white' : '#9e9e9e',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <step.icon />
                  </Box>
                )}
              >
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: isActive ? 'bold' : 'normal',
                    color: isActive ? step.color : 'text.secondary'
                  }}
                >
                  {step.label}
                </Typography>
              </StepLabel>
            </Step>
          );
        })}
      </Stepper>

      {currentStep && (
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            p: 2,
            borderRadius: 1,
            backgroundColor: 'rgba(25, 118, 210, 0.04)',
            border: '1px solid rgba(25, 118, 210, 0.12)'
          }}
        >
          <currentStep.icon sx={{ color: currentStep.color }} />
          <Typography variant="body1">
            Aktueller Status: <strong>{currentStep.label}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
            {currentStep.description}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// State metadata for UI display
const STATE_METADATA: Record<WorkflowState, {
  label: string;
  description: string;
  color: string;
  icon: any;
  category: string;
}> = {
  // Customer states
  'case_created': {
    label: 'Kundenfall erstellt',
    description: 'Neuer Kundenfall wurde angelegt',
    color: '#2196f3',
    icon: InfoIcon,
    category: 'start'
  },
  'preprocessing_decision': {
    label: 'Vorverarbeitung entscheiden',
    description: 'Entscheidung: Sind Lieferantenangebote nötig?',
    color: '#ff9800',
    icon: ProcessIcon,
    category: 'decision'
  },
  'preprocessing_needed': {
    label: 'Vorverarbeitung erforderlich',
    description: 'Lieferantenangebote zu Positionen hinzufügen',
    color: '#9c27b0',
    icon: ConfigIcon,
    category: 'processing'
  },
  'waiting_for_customer_decision': {
    label: 'Kundenentscheidung ausstehend',
    description: 'Kunde entscheidet über Annahme/Ablehnung',
    color: '#ff5722',
    icon: WarningIcon,
    category: 'waiting'
  },
  'case_won': {
    label: 'Fall gewonnen',
    description: 'Kunde hat Angebot angenommen',
    color: '#4caf50',
    icon: CompleteIcon,
    category: 'success'
  },
  'case_lost': {
    label: 'Fall verloren',
    description: 'Kunde hat Angebot abgelehnt',
    color: '#f44336',
    icon: WarningIcon,
    category: 'terminal'
  },
  'waiting_for_file_upload': {
    label: 'Datei-Upload ausstehend',
    description: 'Warten auf vollständige Dateien',
    color: '#3f51b5',
    icon: ProcessIcon,
    category: 'waiting'
  },
  'completed': {
    label: 'Abgeschlossen',
    description: 'Workflow vollständig abgeschlossen',
    color: '#388e3c',
    icon: CompleteIcon,
    category: 'terminal'
  },
  
  // Supplier states
  'supplier_case_created': {
    label: 'Lieferantenfall erstellt',
    description: 'Neuer Lieferantenfall angelegt',
    color: '#2196f3',
    icon: InfoIcon,
    category: 'start'
  },
  'supplier_quote_generated': {
    label: 'Angebot generiert',
    description: 'Angebot erstellt, bereit zum Versenden',
    color: '#9c27b0',
    icon: ProcessIcon,
    category: 'processing'
  },
  'supplier_contacted': {
    label: 'Lieferant kontaktiert',
    description: 'E-Mail versendet, warten auf Antwort',
    color: '#ff9800',
    icon: WarningIcon,
    category: 'waiting'
  },
  'supplier_response_received': {
    label: 'Antwort erhalten',
    description: 'Antwort erhalten, Auswahl ausstehend',
    color: '#3f51b5',
    icon: ProcessIcon,
    category: 'processing'
  },
  'quote_selected': {
    label: 'Angebot ausgewählt',
    description: 'Angebot ausgewählt, bereit für Konvertierung',
    color: '#4caf50',
    icon: CompleteIcon,
    category: 'success'
  },
  'converted_to_order': {
    label: 'In Bestellung konvertiert',
    description: 'Angebot zu Bestellung konvertiert',
    color: '#388e3c',
    icon: CompleteIcon,
    category: 'terminal'
  },
  'case_closed': {
    label: 'Fall geschlossen',
    description: 'Fall ohne Konvertierung geschlossen',
    color: '#757575',
    icon: CompleteIcon,
    category: 'terminal'
  }
};

interface WorkflowStateManagerProps {
  caseId: string;
  caseType: 'customer_order' | 'supplier_case';
  currentState?: string;
  onStateTransition: (fromState: string, toState: string, metadata?: any) => Promise<void>;
  showNextActions?: boolean;
  compactMode?: boolean;
  readonly?: boolean;
  onComponentAction?: (action: string, data?: any) => void;
}

export const WorkflowStateManager: React.FC<WorkflowStateManagerProps> = ({
  caseId,
  caseType,
  currentState,
  onStateTransition,
  showNextActions = true,
  compactMode = false,
  readonly = false,
  onComponentAction
}) => {
  const { showNotification } = useNotification();
  
  // State management
  const [workflowState, setWorkflowState] = useState<string>(currentState || '');
  const [loading, setLoading] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    targetState: string;
    title: string;
    description: string;
  }>({ open: false, targetState: '', title: '', description: '' });

  // Get workflow configuration based on case type
  const workflowStates = caseType === 'supplier_case' ? SUPPLIER_WORKFLOW_STATES : CUSTOMER_WORKFLOW_STATES;
  const validTransitions = caseType === 'supplier_case' ? SUPPLIER_TRANSITIONS : CUSTOMER_TRANSITIONS;

  // Load current workflow state from API
  useEffect(() => {
    const loadWorkflowState = async () => {
      if (!currentState) {
        try {
          const response = await fetch(`/api/v3/workflow/cases/${caseId}/state`);
          const data = await response.json();
          
          if (data.success) {
            setWorkflowState(data.data.workflow_state);
          }
        } catch (error) {
          console.error('Error loading workflow state:', error);
        }
      } else {
        setWorkflowState(currentState);
      }
    };

    if (caseId) {
      loadWorkflowState();
    }
  }, [caseId, currentState]);

  // Get available next states
  const getAvailableTransitions = (): string[] => {
    return validTransitions[workflowState] || [];
  };

  // Get current state metadata
  const getCurrentStateMetadata = () => {
    return STATE_METADATA[workflowState as WorkflowState] || {
      label: workflowState,
      description: 'Unbekannter Zustand',
      color: '#757575',
      icon: InfoIcon,
      category: 'unknown'
    };
  };

  // Get current state index for progress calculation
  const getCurrentStateIndex = (): number => {
    return workflowStates.indexOf(workflowState);
  };

  // Handle state transition
  const handleStateTransition = async (targetState: string) => {
    if (readonly) return;

    setLoading(true);
    try {
      // Call the transition handler (should persist to backend)
      await onStateTransition(workflowState, targetState);
      
      // Update local state
      setWorkflowState(targetState);
      setConfirmDialog({ open: false, targetState: '', title: '', description: '' });
      
      // Handle navigation based on the new state
      handlePostTransitionNavigation(targetState);
      
      const targetMetadata = STATE_METADATA[targetState as WorkflowState];
      showNotification(
        `Status erfolgreich aktualisiert: ${targetMetadata?.label || targetState}`,
        'success'
      );
    } catch (error: any) {
      console.error('Error transitioning workflow state:', error);
      showNotification(
        `Fehler beim Statuswechsel: ${error.message || 'Unbekannter Fehler'}`,
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  // Handle navigation after state transitions
  const handlePostTransitionNavigation = (newState: string) => {
    let targetTab: string | null = null;
    
    switch (newState) {
      case 'preprocessing_needed':
        targetTab = 'Edit & Supplier Quotes';
        break;
      case 'waiting_for_customer_decision':
        targetTab = 'Angebote auswählen & kompilieren';
        break;
      case 'case_won':
        targetTab = 'Dateiverwaltung';
        break;
      case 'case_lost':
        targetTab = 'Status & Workflow';
        break;
      default:
        break;
    }

    if (targetTab && onComponentAction) {
      // Notify parent component to switch tabs
      onComponentAction('navigate_to_tab', { tabLabel: targetTab });
    }
  };

  // Open confirmation dialog for state transition
  const openTransitionConfirmation = (targetState: string) => {
    const targetMetadata = STATE_METADATA[targetState as WorkflowState];
    setConfirmDialog({
      open: true,
      targetState,
      title: `Übergang zu: ${targetMetadata?.label || targetState}`,
      description: targetMetadata?.description || 'Status ändern?'
    });
  };

  // Handle component-specific actions
  const handleComponentAction = (action: string, data?: any) => {
    if (onComponentAction) {
      onComponentAction(action, data);
    }
  };

  // Render state actions
  const renderStateActions = () => {
    if (readonly || !showNextActions) return null;

    const availableTransitions = getAvailableTransitions();
    
    if (availableTransitions.length === 0) {
      return (
        <Alert severity="info" sx={{ mt: 2 }}>
          Keine weiteren Aktionen verfügbar. Workflow ist abgeschlossen oder blockiert.
        </Alert>
      );
    }

    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Verfügbare Aktionen:
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {availableTransitions.map((nextState) => {
            const stateMetadata = STATE_METADATA[nextState as WorkflowState];
            return (
              <Button
                key={nextState}
                variant="outlined"
                color="primary"
                startIcon={<NextIcon />}
                onClick={() => openTransitionConfirmation(nextState)}
                disabled={loading}
                size={compactMode ? 'small' : 'medium'}
              >
                {stateMetadata?.label || nextState}
              </Button>
            );
          })}
        </Box>
      </Box>
    );
  };

  // Render progress indicator
  const renderProgressIndicator = () => {
    // For supplier cases, use the enhanced visual progress component
    if (caseType === 'supplier_case') {
      return <SupplierWorkflowProgress currentState={workflowState} showLabels={true} size="medium" />;
    }

    // For customer cases, use the original simple progress indicator
    const currentIndex = getCurrentStateIndex();
    const progress = currentIndex >= 0 ? ((currentIndex + 1) / workflowStates.length) * 100 : 0;

    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Workflow-Fortschritt: {Math.round(progress)}% ({currentIndex + 1} von {workflowStates.length} Schritten)
        </Typography>
        <LinearProgress 
          variant="determinate" 
          value={progress} 
          sx={{ 
            height: 8, 
            borderRadius: 4,
            '& .MuiLinearProgress-bar': {
              backgroundColor: getCurrentStateMetadata().color === 'error' ? '#f44336' : '#4caf50'
            }
          }}
        />
      </Box>
    );
  };

  // Render visual workflow steps with checkmarks
  const renderWorkflowSteps = () => {
    const currentIndex = getCurrentStateIndex();
    
    return (
      <Box sx={{ mt: 2, mb: 2 }}>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Workflow-Schritte:
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {workflowStates.map((state, index) => {
            const stateMetadata = STATE_METADATA[state as WorkflowState];
            const isCompleted = index < currentIndex;
            const isCurrent = index === currentIndex;
            const StateIcon = stateMetadata?.icon || InfoIcon;
            
            return (
              <Box key={state} sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                opacity: isCompleted || isCurrent ? 1 : 0.5,
                p: 1,
                borderRadius: 1,
                backgroundColor: isCurrent ? 'action.selected' : 'transparent'
              }}>
                {isCompleted ? (
                  <CompleteIcon sx={{ color: 'success.main', fontSize: 20 }} />
                ) : isCurrent ? (
                  <StateIcon sx={{ color: stateMetadata?.color || 'primary.main', fontSize: 20 }} />
                ) : (
                  <Box sx={{ 
                    width: 20, 
                    height: 20, 
                    borderRadius: '50%', 
                    border: '2px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Box sx={{ 
                      width: 8, 
                      height: 8, 
                      borderRadius: '50%', 
                      backgroundColor: 'divider' 
                    }} />
                  </Box>
                )}
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: isCurrent ? 'bold' : 'normal',
                    color: isCompleted ? 'success.main' : isCurrent ? 'text.primary' : 'text.secondary'
                  }}
                >
                  {stateMetadata?.label || state}
                </Typography>
                {isCurrent && (
                  <Chip 
                    label="AKTUELL" 
                    size="small" 
                    color="primary" 
                    variant="filled" 
                    sx={{ ml: 'auto' }}
                  />
                )}
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };

  // Render current state display
  const renderCurrentState = () => {
    const stateMetadata = getCurrentStateMetadata();
    const StateIcon = stateMetadata.icon;

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <StateIcon sx={{ color: stateMetadata.color, fontSize: compactMode ? 24 : 32 }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant={compactMode ? "subtitle1" : "h6"} gutterBottom>
            {stateMetadata.label}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {stateMetadata.description}
          </Typography>
        </Box>
        <Chip 
          label={workflowState}
          color="primary"
          variant="outlined"
          size={compactMode ? "small" : "medium"}
        />
      </Box>
    );
  };

  if (!workflowState) {
    return (
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={24} />
            <Typography>Workflow-Status wird geladen...</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      <Card elevation={compactMode ? 1 : 3}>
        <CardContent>
          {!compactMode && (
            <Typography variant="h6" gutterBottom>
              🎪 Workflow-Status
            </Typography>
          )}
          
          {!compactMode && renderProgressIndicator()}
          {!compactMode && renderWorkflowSteps()}
          {renderCurrentState()}
          {renderStateActions()}
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}>
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <Typography>{confirmDialog.description}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}>
            Abbrechen
          </Button>
          <Button 
            onClick={() => handleStateTransition(confirmDialog.targetState)}
            color="primary"
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : 'Bestätigen'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WorkflowStateManager;
